import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Settings, Zap, User, Wrench } from 'lucide-react';

export const equipmentTroubleshooting: Playbook = {
  meta: {
    id: 'equipment-troubleshooting',
    slug: 'equipment-troubleshooting',
    categoryId: 'physical-world',
    name: 'The Equipment Support Guide',
    tagline: 'Put a technician on every machine.',
    description: 'Attach QR codes to equipment, appliances, or machinery. Users scan for instant troubleshooting, maintenance tips, manual access, and direct support escalation.',
    strategy: 'Attach QR codes to equipment, appliances, or machinery. Users scan for instant troubleshooting, maintenance tips, manual access, and direct support escalation.',
    benefits: [
      'Instant troubleshooting guidance',
      'Reduce service call costs',
      '24/7 equipment support',
      'Preventive maintenance reminders'
    ],
    targetAudience: ['Manufacturing', 'Office environments', 'Medical facilities'],
    estimatedSetupTime: '30 minutes',
    difficulty: 'intermediate',
    tags: ['equipment', 'troubleshooting', 'maintenance', 'technical-support'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Equipment Troubleshooting Playbook - QR Code Technical Support',
    metaDescription: 'Provide instant equipment support with QR code Genies. Troubleshooting, maintenance, and technical guidance on every machine.',
    keywords: ['equipment support', 'troubleshooting guide', 'machinery assistance', 'technical support', 'qr code maintenance']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "🔧 Hi! I'm your Genie technician for this printer. I can help with troubleshooting, maintenance, or ordering supplies. What seems to be the issue?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "I'm here to help with this printer. What's the problem?" },
    { speaker: 'user', message: "It's showing error code E-02" },
    { speaker: 'agent', message: "That's a paper jam error. I'll walk you through clearing it step by step." },
    { speaker: 'user', message: "Okay, I'm ready" },
    { speaker: 'agent', message: "First, open the front panel by pressing the blue release button on the right side." }
  ],
  templateConfig: {
    firstMessage: "🔧 I'm your support guide for this [EQUIPMENT]. How can I help?",
    systemPrompt: "You are an equipment support Genie accessed via QR code on physical equipment. Provide troubleshooting steps, maintenance guidance, and help users resolve issues. Be patient and clear with instructions.",
    tools: ['error_code_database', 'manual_access', 'video_tutorials', 'parts_ordering', 'technician_scheduling']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User encounters equipment issue',
      description: 'Employee or customer experiences problem with machine or equipment'
    },
    {
      type: 'genie',
      icon: Zap,
      genieIcon: 'genie-lamp',
      label: 'Scans QR for instant help',
      description: 'User scans QR code on equipment to access a dedicated technical Genie'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Diagnoses the problem',
      description: 'Genie asks targeted questions to identify issue type and severity'
    },
    {
      type: 'action',
      icon: Wrench,
      genieIcon: 'settings',
      label: 'Provides step-by-step fix',
      description: 'Delivers clear troubleshooting instructions with visual aids if available'
    },
    {
      type: 'action',
      icon: Settings,
      genieIcon: 'calendar-check',
      label: 'Escalates if needed',
      description: 'Schedules technician visit or orders parts when self-service isn\'t sufficient'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Issue resolved quickly',
      description: 'Equipment back online with minimal downtime and reduced service costs'
    }
  ],
  metrics: {
    conversionRate: '74%',
    responseTime: '<1 second',
    satisfactionScore: '4.5/5',
    costSavings: '$75k/year',
    timeToValue: '2 minutes'
  },
  testimonials: [
    {
      companyName: 'PrintSolutions Corp',
      authorName: 'James Martinez',
      authorTitle: 'Facilities Manager',
      quote: 'Equipment QR codes reduced our service calls by 60%. Most issues are now resolved in minutes instead of hours.',
      metrics: '60% reduction in service calls'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Equipment QR Code Setup',
      description: 'How to attach and configure QR codes for different equipment types',
      url: '/docs/equipment-qr-setup'
    },
    {
      type: 'template',
      title: 'Troubleshooting Decision Trees',
      description: 'Pre-built diagnostic flows for common equipment categories',
      url: '/templates/troubleshooting-trees'
    }
  ]
};